/**
 * The staff shield in a view-as tab (Shardul, 29 Oct 26: "create that shield for zoomlocal users too
 * when impersonation is clicked, after that it shouldnt be shown"). The same purple shield the Fly
 * staff see on /fly-app (AdminUserSwitcher), shown only in the tab Agent Ops opened as a customer
 * (src/lib/viewAs.ts) and only while that view runs: who is being viewed, by whom, until when, and
 * End view. Ending, or reaching VIEW_AS_MINUTES, moves the tab straight to the ended page
 * (src/pages/app/ViewAsStartPage.tsx), which ends the customer's sign-in on the server, empties the
 * tab and closes it, so the shield is never seen again and the customer never sees it at all.
 * Open by default on the first page of a view, a pulsing button after that.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Shield, X } from 'lucide-react';
import { isViewAsTab, VIEW_AS_PATH, VIEW_AS_SHIELD_SEEN_KEY as SEEN_KEY, viewAsMeta } from '@/lib/viewAs';

const IST = 'Asia/Kolkata';
const dayOf = (iso: string) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: IST }).format(new Date(iso));
const timeOf = (iso: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: IST }).format(new Date(iso));

export function ViewAsShield() {
  const { pathname } = useLocation(); // read the view again on every page of the tab
  // Not on the start and ended page itself: that page says what is happening.
  const meta = isViewAsTab() && pathname !== VIEW_AS_PATH ? viewAsMeta() : null;
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(SEEN_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const [ending, setEnding] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const endingRef = useRef(false);

  // Straight to the ended page, which signs out there: signing out here first would send the app
  // to its sign-in page for a moment.
  const end = useCallback((reason: 'asked' | 'time') => {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    window.location.replace(`${VIEW_AS_PATH}?ended=${reason === 'time' ? 'time' : '1'}`);
  }, []);

  const endsAt = meta ? Date.parse(meta.ends_at) : NaN;
  useEffect(() => {
    if (!Number.isFinite(endsAt)) return;
    const t = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, [endsAt]);
  useEffect(() => {
    if (Number.isFinite(endsAt) && now >= endsAt) end('time');
  }, [now, endsAt, end]);

  if (!meta) return null;

  const minimise = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* nothing kept */
    }
  };
  // Read at each render (the tick above re-renders): the shield may have mounted before the view began.
  const minutesLeft = Math.max(0, Math.ceil((endsAt - Math.max(now, Date.now())) / 60_000));

  if (!open) {
    return (
      <div className="fixed bottom-24 right-4 z-[60] sm:right-8">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Impersonating ${meta.name}. Open the staff shield`}
          title={`Impersonating ${meta.email}`}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2"
        >
          <Shield className="h-5 w-5" />
        </button>
        <span className="pointer-events-none absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full bg-orange-500" />
      </div>
    );
  }

  return (
    <section
      aria-label="Staff shield, impersonating a customer"
      className="fixed bottom-24 right-4 z-[60] w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl sm:right-8"
    >
      <header className="flex items-center justify-between gap-2 px-4 pb-2 pt-3">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <Shield className="h-4 w-4 text-purple-600" />
          Impersonating a customer
        </p>
        <button type="button" onClick={minimise} aria-label="Make the shield small" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="space-y-3 px-4 pb-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" title={meta.name}>{meta.name}</p>
          <p className="truncate text-xs text-slate-500" title={meta.email}>{meta.email}</p>
        </div>
        <p className="text-xs text-slate-500">
          Opened by {meta.by}, {dayOf(meta.started_at)}, {timeOf(meta.started_at)} IST. Ends at {timeOf(meta.ends_at)} IST, in {minutesLeft} min.
        </p>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
          <p className="text-xs font-medium text-orange-700">Anything you do here is done as them: posts, replies, settings and billing.</p>
          <p className="mt-1 text-xs text-orange-700/80">Their own sign-ins are not touched, and this view does not count as their visit.</p>
          <button
            type="button"
            onClick={() => end('asked')}
            disabled={ending}
            className="mt-2.5 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-orange-500 bg-white text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-60"
          >
            {ending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {ending ? 'Ending the view' : 'End view and close tab'}
          </button>
        </div>
      </div>
    </section>
  );
}
